// Public (auth-free) HTTP client — used only by endpoints that are
// explicitly designed to be reached without a session token (the
// shareable Live Wall, today; future anonymous status pages).
//
// Why a separate client?
//   • The standard `apiClient` injects `Authorization` / `branch-id` /
//     `Platform` / `language` on every request via its interceptor
//     (src/api/client.js#setupInterceptors). Those headers are correct
//     for authenticated routes, but they have no meaning on a public
//     endpoint — and if the user is logged in, the bearer header would
//     tie an anonymous-looking request back to their session, which is
//     a privacy footgun for a "shareable wall" use case.
//   • The standard client also surfaces a `Session expired` Swal + hard
//     redirect to `/login` on any 401. For the public client a 401
//     means "this share-token is no longer valid" — we want to render
//     an empty-state, NOT log the viewer out.
//
// Behaviour:
//   • Plain axios instance with only `baseURL` + `timeout`.
//   • Request interceptor adds the HMAC signature header on Live Wall
//     URLs so the backend can reject direct-API scraping (see
//     `liveWallSignature.js`). No auth headers ever leave the browser.
//   • Response interceptor only translates errors — the caller decides
//     what to render. 401 → signature missing/invalid; 410 Gone → token
//     revoked/expired; 404 → unknown token; 5xx → backend trouble.

import axios from 'axios'

import { config } from '../config'
import { HTTP_STATUS } from '../constants'

import { signLiveWallUrl, LIVE_WALL_SIGNATURE_HEADER } from './liveWallSignature'

const publicClient = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Sign Live Wall requests so the backend can verify they came from the
// frontend. Interceptors can return a Promise that resolves to the
// modified config — axios awaits it before sending. The signing is
// async (Web Crypto `subtle.sign` returns a Promise), so we have to
// await here rather than setting headers synchronously.
//
// If the secret is unset (local dev misconfig), we still attach an
// empty header so the backend returns a uniform 401 instead of a
// different error shape — the caller will render the same empty-state.
publicClient.interceptors.request.use(async (cfg) => {
  const url = cfg.url || ''
  if (url.includes('/live-wall/')) {
    const signature = await signLiveWallUrl(url, config.liveWall.hmacSecret)
    cfg.headers = cfg.headers || {}
    cfg.headers[LIVE_WALL_SIGNATURE_HEADER] = signature
  }
  return cfg
})

publicClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
)

// Convenience wrapper — surfaces the error code so callers can branch
// on `gone` (token revoked/expired), `notFound` (bad token),
// `unauthorized` (HMAC signature missing or invalid), etc.
export class PublicApiError extends Error {
  constructor(message, { status, code } = {}) {
    super(message)
    this.name = 'PublicApiError'
    this.status = status
    this.code = code
  }
}

// 401 maps to a dedicated code so future UI (e.g. a "signature mismatch"
// banner) can branch without re-checking status.
const HTTP_UNAUTHORIZED = 401

export const publicApiClient = {
  get: async (url, options = {}) => {
    try {
      return await publicClient.get(url, options)
    } catch (err) {
      const status = err?.response?.status
      if (status === HTTP_STATUS.GONE) {
        throw new PublicApiError('Share link revoked or expired.', { status, code: 'gone' })
      }
      if (status === HTTP_STATUS.NOT_FOUND) {
        throw new PublicApiError('Share link not found.', { status, code: 'notFound' })
      }
      if (status === HTTP_UNAUTHORIZED) {
        throw new PublicApiError(
          'Live Wall signature missing or invalid. Verify VITE_LIVE_WALL_HMAC_SECRET matches the backend.',
          { status, code: 'unauthorized' }
        )
      }
      throw err
    }
  },
}

export default publicClient
